import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["highlighted", "itemComponent", "_useButton", "_useDropdownText", "option", "id", "onMouseEnter", "search", "aria-disabled"];
import { RightIcon } from 'foundations-assets';
import { Box } from 'foundations-components/transitional/components/Box';
import { Button } from 'foundations-components/transitional/components/Button';
import { UnstyledButton } from 'foundations-components/transitional/components/Button/PrivateButton';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import styled from 'foundations-theming/utils';
import { forwardRef, memo } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BLANK_LABEL = '\u00A0';
const StyledButtonWrapper = styled.span.withConfig({
  displayName: "DrilldownResultsItem__StyledButtonWrapper",
  componentId: "sc-16dmcms-0"
})(() => ({
  display: 'block'
}));
const StyledContentLabel = styled.span.withConfig({
  displayName: "DrilldownResultsItem__StyledContentLabel",
  componentId: "sc-16dmcms-1"
})(() => {
  return {
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  };
});
const StyledContentWrapper = styled.span.withConfig({
  displayName: "DrilldownResultsItem__StyledContentWrapper",
  componentId: "sc-16dmcms-2"
})(() => {
  return {
    display: 'flex',
    alignItems: 'center',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap'
  };
});
const StyledItem = styled.li.withConfig({
  displayName: "DrilldownResultsItem__StyledItem",
  componentId: "sc-16dmcms-3"
})(({
  $highlighted,
  theme
}) => {
  const isHighlighted = $highlighted;
  return {
    '&:hover:not([aria-disabled="true"])': {
      cursor: 'pointer'
    },
    backgroundColor: isHighlighted ? theme.color['fill-info-subtle'] : undefined,
    [`& ${UnstyledButton}`]: {
      backgroundColor: isHighlighted ? `${theme.color['fill-info-subtle']} !important` : undefined
    }
  };
});
const MemoizedContent = /*#__PURE__*/memo(({
  renderedLabel
}) => /*#__PURE__*/_jsx(StyledContentWrapper, {
  children: /*#__PURE__*/_jsx(StyledContentLabel, {
    "data-option-text": true,
    children: renderedLabel
  })
}));
const MemoizedButton = /*#__PURE__*/memo(({
  option,
  rest,
  computedTitle,
  renderedContent
}) => {
  if ('children' in option && Array.isArray(option.children) && option.children.length > 0) {
    return /*#__PURE__*/_jsx(Tooltip, {
      placement: "left",
      title: option.disabledTooltip,
      disabled: !option.disabled,
      children: /*#__PURE__*/_jsx(Button, Object.assign({}, rest, {
        paddingBlock: "200",
        paddingInline: "500",
        tabIndex: -1,
        title: computedTitle !== null && computedTitle !== void 0 ? computedTitle : undefined,
        use: "unstyled",
        children: /*#__PURE__*/_jsxs(TFlex, {
          align: "center",
          children: [/*#__PURE__*/_jsx(Box, {
            grow: 1,
            children: renderedContent
          }), /*#__PURE__*/_jsx(Box, {
            marginBlockStart: "100",
            children: /*#__PURE__*/_jsx(RightIcon, {})
          })]
        })
      }))
    });
  }
  return /*#__PURE__*/_jsx(Tooltip, {
    placement: "left",
    title: option.disabledTooltip,
    disabled: !option.disabled,
    children: /*#__PURE__*/_jsx(StyledButtonWrapper, {
      onClick: rest.onClick,
      children: /*#__PURE__*/_jsx(Button, Object.assign({}, rest, {
        paddingBlock: "200",
        paddingInline: "500",
        tabIndex: -1,
        title: computedTitle,
        use: "unstyled",
        disabled: option.disabled,
        __color: option.disabled ? 'text-primary-disabled' : 'inherit',
        children: renderedContent
      }))
    })
  });
});
const getLoadingIndicator = computedButtonText => {
  return /*#__PURE__*/_jsxs(TFlex, {
    align: "center",
    children: [computedButtonText, /*#__PURE__*/_jsx(LoadingSpinner, {
      marginBlock: "0",
      marginInline: "0",
      marginInlineStart: "100",
      size: "xs",
      use: "tertiary"
    })]
  });
};
const getLabel = ({
  option: {
    buttonText,
    dropdownText,
    loading,
    text,
    value
  },
  _useDropdownText
}) => {
  const fallbackText = text || (value == null ? '' : value.toString());
  if (_useDropdownText) {
    return dropdownText == null ? fallbackText : dropdownText;
  }
  const computedButtonText = buttonText == null ? fallbackText : buttonText;
  if (loading) {
    return getLoadingIndicator(`${computedButtonText}`);
  } else {
    return computedButtonText;
  }
};
const getTitle = ({
  option: {
    text,
    title,
    value
  }
}) => {
  if (title === '') {
    return undefined;
  }
  return title || text || (value == null ? '' : value.toString());
};
const renderLabel = ({
  search,
  computedLabel
}) => {
  if (!computedLabel) {
    return BLANK_LABEL;
  }
  if (!search || typeof computedLabel !== 'string') {
    return computedLabel;
  }
  const searchResult = search.exec(computedLabel);
  if (!searchResult) {
    return computedLabel;
  }
  const match = searchResult[0];
  const head = computedLabel.slice(0, searchResult.index);
  const tail = computedLabel.slice(searchResult.index + match.length);
  return /*#__PURE__*/_jsxs("span", {
    children: [head, /*#__PURE__*/_jsx("span", {
      children: match
    }), tail]
  });
};
const DrilldownResultsItem = /*#__PURE__*/memo( /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
      highlighted = false,
      itemComponent: Item = 'li',
      _useButton = true,
      _useDropdownText = true,
      option,
      id,
      onMouseEnter,
      search,
      'aria-disabled': ariaDisabled = false
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  if (option.rawNode) return option.rawNode;
  const computedTitle = getTitle({
    option
  });
  const computedLabel = getLabel({
    option,
    _useDropdownText
  });
  const renderedLabel = renderLabel({
    search,
    computedLabel
  });
  let renderedContent = /*#__PURE__*/_jsx(MemoizedContent, {
    renderedLabel: renderedLabel
  });
  if (_useButton) {
    renderedContent = /*#__PURE__*/_jsx(MemoizedButton, {
      option: option,
      rest: rest,
      computedTitle: computedTitle,
      renderedContent: renderedContent
    });
  }
  return /*#__PURE__*/_jsx(StyledItem, {
    $highlighted: highlighted,
    as: Item,
    option: option,
    "aria-selected": !!highlighted,
    "aria-disabled": option.disabled || ariaDisabled || undefined,
    "data-test-id": `test-drilldown-option-${option.value}`,
    "data-option-value": option.value,
    id: id,
    onMouseEnter: onMouseEnter,
    role: "option",
    ref: ref,
    "data-typeahead-item": true,
    children: renderedContent
  });
}));
export default DrilldownResultsItem;